/**
 * The revision-bound recalculation status of the user's saved Financial
 * settings (saved status + SSE). It reports only: queued, updating with
 * progress, briefly "updated" after a run this view saw, failed, or live status
 * unavailable. Idle shows nothing.
 */
import { useEffect, useRef, useState } from 'react'
import { IconAlertCircle, IconCheck, IconClockHour4, IconInfoCircle, IconWifiOff } from '@tabler/icons-react'
import { useFinancialUpdateStatus, type FinancialUpdateConnection, type FinancialUpdateStatus as Snapshot } from '../../../api/financialUpdates'
import { Popover } from '../../../ui/kit'

const time = (value: string | null) => value ? new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(value)) : null

function statusLabel(status: Snapshot): string {
  if (status.state === 'scheduled') return 'Results update queued'
  if (status.state === 'running') return 'Updating results'
  if (status.state === 'completed') return 'Results updated'
  if (status.state === 'failed') return 'Results update failed'
  return ''
}

function StatusView({ status, connection, error, showCompleted }: { status?: Snapshot; connection: FinancialUpdateConnection; error: string | null; showCompleted: boolean }) {
  const [open, setOpen] = useState(false)
  const visible = status && status.state !== 'idle' && (status.state !== 'completed' || showCompleted) ? status : undefined
  const unavailable = error !== null || connection === 'unavailable'
  if (!visible && !unavailable) return null
  const state = visible?.state ?? 'unavailable'
  const text = visible ? statusLabel(visible) : 'Results status unavailable'
  const stale = connection === 'reconnecting' || connection === 'unavailable'
  const progress = visible?.state === 'running' && visible.progress.total !== null && visible.progress.total > 0 ? `${visible.progress.completed.toLocaleString('en-GB')} / ${visible.progress.total.toLocaleString('en-GB')}` : null
  const queuedFor = visible?.state === 'scheduled' ? time(visible.scheduledFor) : null
  return <Popover open={open} onClose={() => setOpen(false)} align="end" width={340} anchor={
    <button type="button" className="fin-upd" data-state={state} data-connection={connection} onClick={() => setOpen(value => !value)} aria-haspopup="dialog" aria-expanded={open} aria-label={`${text}${progress ? `, ${progress} results` : ''}${stale ? '. Live status unavailable' : ''}. Show details`}>
      <span className="fin-upd-ic" aria-hidden="true">{stale ? <IconWifiOff className="ic-sm" /> : state === 'running' ? <span className="spin" /> : state === 'scheduled' ? <IconClockHour4 className="ic-sm" /> : state === 'completed' ? <IconCheck className="ic-sm" /> : <IconAlertCircle className="ic-sm" />}</span>
      <span role="status" aria-live="polite" aria-atomic="true">{text}{progress ? <span className="fin-upd-progress">{progress}</span> : null}</span>
      <IconInfoCircle className="ic-sm fin-upd-help" aria-hidden="true" />
    </button>
  }>
    <div className="pop-title">{text}</div>
    {state === 'scheduled' ? <p className="pop-note">Your settings are saved. {queuedFor ? `The next update is scheduled for ${queuedFor}. ` : ''}Repeated saves are combined so only your latest settings are used.</p> : null}
    {state === 'running' ? <p className="pop-note">Your saved settings are being applied across your properties.{progress ? ` ${progress} results processed.` : ''} You can continue using the workspace.</p> : null}
    {state === 'completed' ? <p className="pop-note">Your property results have been updated using your latest saved settings.</p> : null}
    {state === 'failed' ? <p className="pop-note">{visible?.error || 'The property results update could not finish.'}</p> : null}
    {connection === 'reconnecting' ? <p className="pop-note">Reconnecting to live status. The last reported status is shown.</p> : null}
    {unavailable ? <p className="pop-note">{error || 'Live status is unavailable. Refresh to check the latest update.'}</p> : null}
  </Popover>
}

export function FinancialUpdateStatus({ actorId, enabled }: { actorId: string; enabled: boolean }) {
  const status = useFinancialUpdateStatus(actorId, enabled)
  const lastBusyRevision = useRef<string | null>(null)
  const [completedRevision, setCompletedRevision] = useState<string | null>(null)
  useEffect(() => { lastBusyRevision.current = null; setCompletedRevision(null) }, [actorId])
  useEffect(() => {
    const snapshot = status.data
    if (!snapshot) return
    if (snapshot.state === 'scheduled' || snapshot.state === 'running') { lastBusyRevision.current = snapshot.revision; setCompletedRevision(null); return }
    if (snapshot.state !== 'completed') { setCompletedRevision(null); return }
    if (lastBusyRevision.current !== snapshot.revision) return
    lastBusyRevision.current = null
    setCompletedRevision(snapshot.revision)
  }, [status.data])
  useEffect(() => {
    if (completedRevision === null) return
    // A brief acknowledgement is local presentation, not a data-refresh timer.
    const timer = setTimeout(() => setCompletedRevision(null), 8_000)
    return () => clearTimeout(timer)
  }, [completedRevision])
  if (!enabled) return null
  return <StatusView status={status.data} connection={status.connection} error={status.error?.message ?? status.streamError} showCompleted={completedRevision !== null && completedRevision === status.data?.revision} />
}
